import { useState } from "react";
import { ActionModal, FIELD_LABEL_CLASS, INPUT_CLASS } from "./ActionModal";
import { useLockRemovalPreview } from "../../hooks/useTrackingHealth";
import { LOCK_EXPLANATIONS } from "../../vocabulary";
import type { TrackingLockKind } from "../../types";

/** The lock event log requires non-empty text server-side; an operator who
 *  skips the field gets this honest placeholder instead of a blocked click. */
const NO_REASON_GIVEN = "(no reason given)";

/**
 * Apply / remove a lock (§5.2). The explainer leads — what THIS lock does is
 * the thing an operator needs before confirming; the reason is optional.
 * Removal fetches the backend-computed consequence (the removal's own
 * dry-run path) so the confirm copy and the act cannot disagree.
 */
export function LockActionModal({
  customerId,
  lock,
  action,
  onSubmit,
  onClose,
  pending,
}: {
  customerId: string;
  lock: TrackingLockKind;
  action: "apply" | "remove";
  onSubmit: (reason: string) => void;
  onClose: () => void;
  pending?: boolean;
}) {
  const [reason, setReason] = useState("");
  const preview = useLockRemovalPreview(customerId, lock, action === "remove");
  // The endpoint refuses a removal while the hotel is Paused, so the button is
  // disabled rather than left to 409. `removable` is only meaningful once the
  // preview has loaded; until then nothing is blocked.
  const blocked = action === "remove" && preview.data?.removable === false;

  return (
    <ActionModal
      title={`${action === "apply" ? "Apply" : "Remove"} ${lock} lock`}
      submitLabel={action === "apply" ? "Apply lock" : "Remove lock"}
      onSubmit={() => onSubmit(reason.trim() || NO_REASON_GIVEN)}
      onClose={onClose}
      pending={pending}
      disabled={blocked}
      danger={action === "remove"}
    >
      <div className="px-3 py-2.5 rounded-md bg-[#F4F4F6] text-sm text-content-secondary">
        <span className="font-medium text-content-primary capitalize">
          {lock} lock:
        </span>{" "}
        {LOCK_EXPLANATIONS[lock]}
      </div>
      {action === "remove" && (
        <div
          className={`px-3 py-2.5 rounded-md text-sm ${
            blocked
              ? "bg-[#FEE2E2] text-danger-dark"
              : "bg-[#FEF3C7] text-warning-dark"
          }`}
        >
          {preview.isLoading && "Computing the consequence…"}
          {preview.isError &&
            "Could not compute the removal consequence — the removal itself will still report it."}
          {/* A paused hotel's locks cannot be removed at all (ADR 0040), so
              the refusal replaces the trusted-data-start-date copy entirely:
              there is no consequence to warn about, only a reason and the way
              out. */}
          {preview.data?.blockedReason}
          {preview.data &&
            !blocked &&
            (preview.data.tdsdReset
              ? `Removing this lock resets the trusted data start date (${preview.data.skippedRuns ?? 0} daily rule runs were skipped)${
                  preview.data.previousTdsd && preview.data.newTdsd
                    ? `: ${preview.data.previousTdsd} → ${preview.data.newTdsd}`
                    : ""
                }`
              : "Removing this lock will NOT reset the trusted data start date (no runs were skipped)")}
        </div>
      )}
      <div>
        <label className={FIELD_LABEL_CLASS}>Reason (optional)</label>
        <textarea
          className={`${INPUT_CLASS} min-h-[66px]`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Logged on the lock event — leave empty if there's nothing to say."
          autoFocus
        />
      </div>
    </ActionModal>
  );
}
